<script setup>
import { onMounted, computed, ref, reactive, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import { useUISettings } from 'dashboard/composables/useUISettings';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import camelcaseKeys from 'camelcase-keys';

// Returns the correct API payload regardless of whether filters are flat or grouped
const buildApiPayload = filters => {
  if (!filters) return filterQueryGenerator([]);
  // New grouped format: { groups: [...] }
  if (filters.groups) return filters;
  // Legacy flat array format
  if (Array.isArray(filters)) return filterQueryGenerator(filters);
  return filters;
};

import ContactsListLayout from 'dashboard/components-next/Contacts/ContactsListLayout.vue';
import ContactEmptyState from 'dashboard/components-next/Contacts/EmptyState/ContactEmptyState.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContactsList from 'dashboard/components-next/Contacts/Pages/ContactsList.vue';
import ContactsTableView from 'dashboard/components-next/Contacts/Pages/ContactsTableView.vue';
import ContactsBulkActionBar from '../components/ContactsBulkActionBar.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import BulkActionsAPI from 'dashboard/api/bulkActions';
import ContactAPI from 'dashboard/api/contacts';

const DEFAULT_SORT_FIELD = 'last_activity_at';
const DEBOUNCE_DELAY = 300;
const GROUP_BY_BATCH_SIZE = 500;
const GROUP_BY_MAX_CONTACTS = 5000;

// Helper: fetch all contacts in batches to avoid server timeout
const fetchAllInBatches = async (fetchFn, total) => {
  const cap = Math.min(total, GROUP_BY_MAX_CONTACTS);
  const totalPages = Math.ceil(cap / GROUP_BY_BATCH_SIZE);
  const allPayload = [];
  for (let p = 1; p <= totalPages; p += 1) {
    // eslint-disable-next-line no-await-in-loop
    const response = await fetchFn(p, GROUP_BY_BATCH_SIZE);
    const items = response.data?.payload || [];
    allPayload.push(
      ...camelcaseKeys(items, { deep: true, stopPaths: ['custom_attributes'] })
    );
    if (items.length < GROUP_BY_BATCH_SIZE) break;
  }
  return allPayload;
};

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { updateUISettings, uiSettings } = useUISettings();

const segments = useMapGetter('customViews/getContactCustomViews');
const appliedFilters = useMapGetter('contacts/getAppliedContactFilters');
const meta = useMapGetter('contacts/getMeta');

const searchQuery = computed(() => route.query?.search);
const searchValue = ref(searchQuery.value || '');
const pageNumber = computed(() => Number(route.query?.page) || 1);

const parseSortSettings = (sortString = '') => {
  const hasDescending = sortString.startsWith('-');
  const sortField = hasDescending ? sortString.slice(1) : sortString;
  return {
    sort: sortField || DEFAULT_SORT_FIELD,
    order: hasDescending ? '-' : '',
  };
};

const {
  contacts_sort_by: contactSortBy = '',
  contacts_view_type: savedViewType = 'card',
} = uiSettings.value ?? {};
const { sort: initialSort, order: initialOrder } =
  parseSortSettings(contactSortBy);

const sortState = reactive({
  activeSort: initialSort,
  activeOrdering: initialOrder,
});

const viewType = ref(savedViewType);
const isTableView = computed(() => viewType.value === 'table');

const toggleViewType = async () => {
  viewType.value = viewType.value === 'card' ? 'table' : 'card';
  await updateUISettings({
    contacts_view_type: viewType.value,
  });
};

const activeLabel = computed(() => route.params.label);
const activeSegmentId = computed(() => route.params.segmentId);
const currentPage = computed(() => Number(meta.value?.currentPage));
const totalItems = computed(() => meta.value?.count);

const selectedContactIds = ref([]);
const isSelectAllActive = ref(false); // Track if "select all" was used (includes contacts beyond current page)
const isBulkActionLoading = ref(false);
const bulkDeleteDialogRef = ref(null);
const selectedCount = computed(() => selectedContactIds.value.length);

// For infinite scroll in table view - accumulated contacts list
const allContacts = ref([]);
const isLoadingMore = ref(false);
// Guards fetchContactsBasedOnContext from running concurrently with handleLoadMore
const isFetchingFresh = ref(false);
// Incremented on every fresh fetch — TableView uses this to distinguish rebuild vs append
const freshFetchKey = ref(0);

// ─────────────────────────────────────────────────────────────────────────────

const hasMoreContacts = computed(() => {
  if (!meta.value) return false;
  const { count } = meta.value;
  return allContacts.value.length < count;
});

const bulkDeleteDialogTitle = computed(() =>
  selectedCount.value > 1
    ? t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.TITLE')
    : t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.SINGULAR_TITLE')
);
const bulkDeleteDialogDescription = computed(() =>
  selectedCount.value > 1
    ? t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.DESCRIPTION', {
        count: selectedCount.value,
      })
    : t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.SINGULAR_DESCRIPTION')
);
const bulkDeleteDialogConfirmLabel = computed(() =>
  selectedCount.value > 1
    ? t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.CONFIRM_MULTIPLE')
    : t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.CONFIRM_SINGLE')
);
const hasSelection = computed(() => selectedCount.value > 0);
const activeSegment = computed(() => {
  if (!activeSegmentId.value) return undefined;
  return segments.value.find(view => view.id === Number(activeSegmentId.value));
});

const hasContacts = computed(() => allContacts.value.length > 0);
const isContactIndexView = computed(
  () => route.name === 'contacts_dashboard_index' && pageNumber.value === 1
);
const isActiveView = computed(() => route.name === 'contacts_dashboard_active');
const hasAppliedFilters = computed(() => {
  const f = appliedFilters.value;
  if (!f) return false;
  if (f.groups) return f.groups.some(g => g.conditions?.length > 0);
  return Array.isArray(f) && f.length > 0;
});

const showEmptyStateLayout = computed(() => {
  return (
    !searchValue.value &&
    !hasContacts.value &&
    isContactIndexView.value &&
    !hasAppliedFilters.value
  );
});
const showEmptyText = computed(() => {
  return (
    (searchValue.value ||
      hasAppliedFilters.value ||
      !isContactIndexView.value) &&
    !hasContacts.value
  );
});

const headerTitle = computed(() => {
  if (searchValue.value) return t('CONTACTS_LAYOUT.HEADER.SEARCH_TITLE');
  if (isActiveView.value) return t('CONTACTS_LAYOUT.HEADER.ACTIVE_TITLE');
  if (activeSegmentId.value) return activeSegment.value?.name;
  if (activeLabel.value) return `#${activeLabel.value}`;
  return t('CONTACTS_LAYOUT.HEADER.TITLE');
});

const emptyStateMessage = computed(() => {
  if (isActiveView.value)
    return t('CONTACTS_LAYOUT.EMPTY_STATE.ACTIVE_EMPTY_STATE_TITLE');
  if (!searchValue.value || hasAppliedFilters.value)
    return t('CONTACTS_LAYOUT.EMPTY_STATE.LIST_EMPTY_STATE_TITLE');
  return t('CONTACTS_LAYOUT.EMPTY_STATE.SEARCH_EMPTY_STATE_TITLE');
});

const visibleContactIds = computed(() =>
  allContacts.value.map(contact => contact.id)
);

const lastSelectedContactId = ref(null);

// Track current context to detect actual changes vs. initial load
const lastContext = ref({
  label: null,
  segmentId: null,
  isActive: false,
  initialized: false,
});

const clearSelection = () => {
  selectedContactIds.value = [];
  isSelectAllActive.value = false;
  lastSelectedContactId.value = null;
};

const openBulkDeleteDialog = () => {
  if (!selectedContactIds.value.length || isBulkActionLoading.value) return;
  bulkDeleteDialogRef.value?.open?.();
};

const toggleSelectAll = async shouldSelect => {
  if (!shouldSelect) {
    selectedContactIds.value = [];
    isSelectAllActive.value = false;
    return;
  }

  // Load all contact IDs from the current filter/view
  try {
    let response;
    if (hasAppliedFilters.value || activeSegment.value?.query) {
      // Filter view - get all IDs matching the filter
      const queryPayload =
        activeSegment.value?.query || buildApiPayload(appliedFilters.value);
      response = await ContactAPI.filterIds(queryPayload);
    } else {
      // Normal view - get all IDs (optionally filtered by label)
      const sortAttr = `${sortState.activeOrdering}${sortState.activeSort}`;
      response = await ContactAPI.getAllIds(sortAttr, activeLabel.value);
    }
    selectedContactIds.value = response.data.contact_ids || [];
    isSelectAllActive.value = true;
  } catch (error) {
    // Fallback to visible contacts only
    selectedContactIds.value = [...visibleContactIds.value];
    isSelectAllActive.value = false;
    useAlert(t('CONTACTS_BULK_ACTIONS.SELECT_ALL_FAILED'));
  }
};

const toggleContactSelection = ({
  id,
  value,
  isShiftKey = false,
  orderedIds,
}) => {
  const isAlreadySelected = selectedContactIds.value.includes(id);
  const shouldSelect = value ?? !isAlreadySelected;

  // When manually deselecting, reset "select all" state since selection is no longer complete
  if (!shouldSelect) {
    isSelectAllActive.value = false;
  }

  if (isShiftKey && lastSelectedContactId.value) {
    // Use visual order from table (respects grouping) if provided, else fall back to raw list
    const scopeIds = orderedIds?.length
      ? orderedIds
      : allContacts.value.map(c => c.id);

    const startIndex = scopeIds.indexOf(lastSelectedContactId.value);
    const endIndex = scopeIds.indexOf(id);

    if (startIndex !== -1 && endIndex !== -1) {
      const from = Math.min(startIndex, endIndex);
      const to = Math.max(startIndex, endIndex);
      const rangeIds = scopeIds.slice(from, to + 1);

      if (shouldSelect) {
        selectedContactIds.value = Array.from(
          new Set([...selectedContactIds.value, ...rangeIds])
        );
      } else {
        selectedContactIds.value = selectedContactIds.value.filter(
          contactId => !rangeIds.includes(contactId)
        );
      }

      lastSelectedContactId.value = id;
      return;
    }
  }

  if (shouldSelect && !isAlreadySelected) {
    selectedContactIds.value = [...selectedContactIds.value, id];
  } else if (!shouldSelect && isAlreadySelected) {
    selectedContactIds.value = selectedContactIds.value.filter(
      contactId => contactId !== id
    );
  }

  lastSelectedContactId.value = id;
};

const updatePageParam = (page, search = '') => {
  const query = {
    ...route.query,
    page: page.toString(),
    ...(search ? { search } : {}),
  };

  if (!search) {
    delete query.search;
  }

  router.replace({ query });
};

const buildSortAttr = () =>
  `${sortState.activeOrdering}${sortState.activeSort}`;

const handleLoadMore = async () => {
  if (isLoadingMore.value || isFetchingFresh.value || !hasMoreContacts.value)
    return;

  isLoadingMore.value = true;
  const nextPage = currentPage.value + 1;

  try {
    // Fetch directly via API to get raw data
    const sortAttr = buildSortAttr();
    let response;

    if (searchValue.value) {
      response = await ContactAPI.search(
        encodeURIComponent(searchValue.value),
        nextPage,
        sortAttr,
        activeLabel.value
      );
    } else if (isActiveView.value) {
      response = await ContactAPI.active(nextPage, sortAttr);
    } else if (hasAppliedFilters.value || activeSegment.value?.query) {
      const queryPayload =
        activeSegment.value?.query || buildApiPayload(appliedFilters.value);
      response = await ContactAPI.filter(nextPage, sortAttr, queryPayload);
    } else {
      response = await ContactAPI.get(nextPage, sortAttr, activeLabel.value);
    }

    // Append new contacts to our local list — deduplicate by ID to prevent doubles
    if (response.data?.payload) {
      const newContacts = camelcaseKeys(response.data.payload, {
        deep: true,
        stopPaths: ['custom_attributes'],
      });
      const existingIds = new Set(allContacts.value.map(c => c.id));
      const unique = newContacts.filter(c => !existingIds.has(c.id));
      if (unique.length > 0) {
        allContacts.value = [...allContacts.value, ...unique];
      }
    }

    // Always update meta — ensures hasMoreContacts recalculates correctly
    if (response.data?.meta) {
      store.commit('contacts/SET_CONTACT_META', response.data.meta);
    }

    // If API returned empty or fewer items than a full page, we've reached the end
    const payload = response.data?.payload;
    if (!payload || payload.length === 0) {
      // Force meta count to match current length so hasMoreContacts = false
      store.commit('contacts/SET_CONTACT_META', {
        ...(meta.value || {}),
        count: allContacts.value.length,
      });
    }
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.LOAD_MORE_FAILED'));
  } finally {
    isLoadingMore.value = false;
  }
};

const fetchContactsBasedOnContext = async page => {
  updatePageParam(page, searchValue.value);
  if (isFetchingFresh.value) return;

  isFetchingFresh.value = true;

  if (isLoadingMore.value) {
    isLoadingMore.value = false;
  }

  let freshContacts = [];

  try {
    const sortAttr = buildSortAttr();
    const activeGroupBy = uiSettings.value?.contacts_table_group_by;
    const shouldFetchAll = isTableView.value && activeGroupBy;

    if (searchValue.value) {
      clearSelection();
      await store.dispatch('contacts/clearContactFilters');
      // searchValue is already set by the caller
      updatePageParam(page, searchValue.value);
      // First page to get total count
      const firstResponse = await ContactAPI.search(
        encodeURIComponent(searchValue.value),
        page,
        sortAttr,
        activeLabel.value
      );
      if (firstResponse.data?.meta)
        store.commit('contacts/SET_CONTACT_META', firstResponse.data.meta);
      const total = firstResponse.data?.meta?.count || 0;
      if (
        shouldFetchAll &&
        total > (firstResponse.data?.payload?.length || 0)
      ) {
        const query = encodeURIComponent(searchValue.value);
        freshContacts = await fetchAllInBatches(
          (p, perPage) =>
            ContactAPI.search(query, p, sortAttr, activeLabel.value, perPage),
          total
        );
      } else {
        freshContacts = camelcaseKeys(firstResponse.data?.payload || [], {
          deep: true,
          stopPaths: ['custom_attributes'],
        });
      }
    } else {
      searchValue.value = '';
      if (isActiveView.value) {
        await store.dispatch('contacts/clearContactFilters');
        const firstResponse = await ContactAPI.active(page, sortAttr);
        if (firstResponse.data?.meta)
          store.commit('contacts/SET_CONTACT_META', firstResponse.data.meta);
        updatePageParam(page);
        const total = firstResponse.data?.meta?.count || 0;
        if (
          shouldFetchAll &&
          total > (firstResponse.data?.payload?.length || 0)
        ) {
          freshContacts = await fetchAllInBatches(
            (p, perPage) => ContactAPI.active(p, sortAttr, perPage),
            total
          );
        } else {
          freshContacts = camelcaseKeys(firstResponse.data?.payload || [], {
            deep: true,
            stopPaths: ['custom_attributes'],
          });
        }
      } else if (
        (hasAppliedFilters.value || activeSegment.value?.query) &&
        !activeLabel.value
      ) {
        const queryPayload =
          activeSegment.value?.query || buildApiPayload(appliedFilters.value);
        const firstResponse = await ContactAPI.filter(
          page,
          sortAttr,
          queryPayload
        );
        if (firstResponse.data?.meta)
          store.commit('contacts/SET_CONTACT_META', firstResponse.data.meta);
        updatePageParam(page);
        const total = firstResponse.data?.meta?.count || 0;
        if (
          shouldFetchAll &&
          total > (firstResponse.data?.payload?.length || 0)
        ) {
          freshContacts = await fetchAllInBatches(
            (p, perPage) =>
              ContactAPI.filter(p, sortAttr, queryPayload, perPage),
            total
          );
        } else {
          freshContacts = camelcaseKeys(firstResponse.data?.payload || [], {
            deep: true,
            stopPaths: ['custom_attributes'],
          });
        }
      } else {
        await store.dispatch('contacts/clearContactFilters');
        const firstResponse = await ContactAPI.get(
          page,
          sortAttr,
          activeLabel.value
        );
        if (firstResponse.data?.meta)
          store.commit('contacts/SET_CONTACT_META', firstResponse.data.meta);
        updatePageParam(page);
        const total = firstResponse.data?.meta?.count || 0;
        if (
          shouldFetchAll &&
          total > (firstResponse.data?.payload?.length || 0)
        ) {
          freshContacts = await fetchAllInBatches(
            (p, perPage) =>
              ContactAPI.get(p, sortAttr, activeLabel.value, perPage),
            total
          );
        } else {
          freshContacts = camelcaseKeys(firstResponse.data?.payload || [], {
            deep: true,
            stopPaths: ['custom_attributes'],
          });
        }
      }
    }

    allContacts.value = freshContacts;
    freshFetchKey.value += 1;
  } finally {
    isFetchingFresh.value = false;
  }
};

// Debounced API fetch — only the network call is delayed
const debouncedFetch = debounce(async (page = 1) => {
  await fetchContactsBasedOnContext(page);
}, DEBOUNCE_DELAY);

// Search handler — updates searchValue immediately, debounces the API call
const searchContacts = (value, page = 1) => {
  searchValue.value = value || '';
  debouncedFetch(page);
};
const assignLabels = async labels => {
  if (!labels.length || !selectedContactIds.value.length) {
    return;
  }

  isBulkActionLoading.value = true;
  try {
    await BulkActionsAPI.create({
      type: 'Contact',
      ids: selectedContactIds.value,
      labels: { add: labels },
    });
    useAlert(t('CONTACTS_BULK_ACTIONS.ASSIGN_LABELS_SUCCESS'));
    clearSelection();
    await fetchContactsBasedOnContext(pageNumber.value);
  } catch (error) {
    useAlert(t('CONTACTS_BULK_ACTIONS.ASSIGN_LABELS_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const deleteContacts = async () => {
  if (!selectedContactIds.value.length) {
    return;
  }

  isBulkActionLoading.value = true;
  try {
    await BulkActionsAPI.create({
      type: 'Contact',
      ids: selectedContactIds.value,
      action_name: 'delete',
    });
    useAlert(t('CONTACTS_BULK_ACTIONS.DELETE_SUCCESS'));
    clearSelection();
    await fetchContactsBasedOnContext(pageNumber.value);
    bulkDeleteDialogRef.value?.close?.();
  } catch (error) {
    useAlert(t('CONTACTS_BULK_ACTIONS.DELETE_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const bulkAssignAgent = async agentId => {
  if (!selectedContactIds.value.length) return;

  isBulkActionLoading.value = true;
  try {
    // Update each selected contact's custom_attributes with assigned_agent_id
    const promises = selectedContactIds.value.map(contactId =>
      store.dispatch('contacts/update', {
        id: contactId,
        custom_attributes: {
          assigned_agent_id: agentId ? String(agentId) : '',
        },
      })
    );
    await Promise.all(promises);
    useAlert(
      t('CONTACTS_BULK_ACTIONS.ASSIGN_AGENT_SUCCESS', {
        count: selectedContactIds.value.length,
      })
    );
    clearSelection();
    await fetchContactsBasedOnContext(pageNumber.value);
  } catch (error) {
    useAlert(t('CONTACTS_BULK_ACTIONS.ASSIGN_AGENT_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const bulkUpdateField = async ({ field, value }) => {
  if (!selectedContactIds.value.length || !field) return;

  isBulkActionLoading.value = true;
  try {
    const promises = selectedContactIds.value.map(contactId => {
      const payload = { id: contactId };

      if (field === 'name') {
        payload.name = value;
      } else if (field === 'email') {
        payload.email = value;
      } else if (field === 'phoneNumber') {
        payload.phone_number = value;
      } else {
        // Custom attribute
        payload.custom_attributes = { [field]: value };
      }

      return store.dispatch('contacts/update', payload);
    });
    await Promise.all(promises);
    useAlert(
      t('CONTACTS_BULK_ACTIONS.BULK_EDIT_SUCCESS', {
        count: selectedContactIds.value.length,
      })
    );
    clearSelection();
    await fetchContactsBasedOnContext(pageNumber.value);
  } catch (error) {
    useAlert(t('CONTACTS_BULK_ACTIONS.BULK_EDIT_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const handleSort = async ({ sort, order }) => {
  Object.assign(sortState, { activeSort: sort, activeOrdering: order });

  await updateUISettings({
    contacts_sort_by: buildSortAttr(),
  });

  await fetchContactsBasedOnContext(1);
};

const createContact = async contact => {
  await store.dispatch('contacts/create', contact);
};

const handleUpdateContact = async ({ contactId, field, value }) => {
  try {
    // Build the update payload based on field
    const payload = {};

    if (field === 'name') {
      payload.name = value;
    } else if (field === 'email') {
      payload.email = value;
    } else if (field === 'phoneNumber') {
      payload.phone_number = value;
    } else {
      // Handle custom attributes
      payload.custom_attributes = {
        [field]: value,
      };
    }

    // Update via API
    await store.dispatch('contacts/update', {
      id: contactId,
      ...payload,
    });

    useAlert(t('CONTACT_FORM.SUCCESS_MESSAGE'));

    // Refresh the current view to show updated data
    await fetchContactsBasedOnContext(pageNumber.value);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error updating contact:', error);

    // Handle specific error types
    if (error.message && error.message.includes('DUPLICATE_CONTACT')) {
      useAlert(
        t('CONTACT_FORM.DUPLICATE_CONTACT_ERROR') ||
          'A contact with this email or phone number already exists'
      );
    } else {
      useAlert(t('CONTACT_FORM.ERROR_MESSAGE'));
    }
  }
};

const handleClearFilters = async () => {
  await store.dispatch('contacts/clearContactFilters');
  await fetchContactsBasedOnContext(1);
};

watch(hasSelection, value => {
  if (!value) {
    bulkDeleteDialogRef.value?.close?.();
  }
});

watch(
  () => uiSettings.value?.contacts_sort_by,
  newSortBy => {
    if (newSortBy) {
      const { sort, order } = parseSortSettings(newSortBy);
      sortState.activeSort = sort;
      sortState.activeOrdering = order;
    }
  },
  { immediate: true }
);

// Re-fetch contacts when group-by changes so that shouldFetchAll can load all data
watch(
  () => uiSettings.value?.contacts_table_group_by,
  (newGroupBy, oldGroupBy) => {
    // Skip the initial mount — onMounted handles that
    if (oldGroupBy === undefined) return;
    // Only re-fetch when in table view — card view doesn't group
    if (isTableView.value) {
      fetchContactsBasedOnContext(1);
    }
  }
);

watch(
  [activeLabel, activeSegment, isActiveView],
  ([newLabel, newSegment, newIsActive]) => {
    const newSegmentId = newSegment?.id;
    const hasContextChanged =
      lastContext.value.initialized &&
      (lastContext.value.label !== newLabel ||
        lastContext.value.segmentId !== newSegmentId ||
        lastContext.value.isActive !== newIsActive);

    // Update stored context
    lastContext.value = {
      label: newLabel,
      segmentId: newSegmentId,
      isActive: newIsActive,
      initialized: true,
    };

    // Only re-fetch if context actually changed — onMounted handles the initial load
    if (hasContextChanged) {
      clearSelection();
      fetchContactsBasedOnContext(1);
    }
  },
  { deep: true }
);

watch(searchQuery, value => {
  if (isFetchingFresh.value) return;
  // Only react when search is cleared externally (e.g. back button)
  if (!value) {
    searchValue.value = '';
    if (
      isActiveView.value ||
      activeLabel.value ||
      activeSegment.value ||
      hasAppliedFilters.value
    )
      return;
    fetchContactsBasedOnContext(1);
  }
});
onMounted(async () => {
  // Fetch custom attributes for table columns
  store.dispatch('attributes/get');

  // Initialize lastContext so the watcher doesn't re-fetch on mount
  lastContext.value = {
    label: activeLabel.value,
    segmentId: activeSegment.value?.id ?? null,
    isActive: isActiveView.value,
    initialized: true,
  };

  await fetchContactsBasedOnContext(pageNumber.value);
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-background"
  >
    <ContactsListLayout
      :search-value="searchValue"
      :header-title="headerTitle"
      :current-page="currentPage"
      :total-items="totalItems"
      :show-pagination-footer="!isFetchingFresh && hasContacts && !isTableView"
      :active-sort="sortState.activeSort"
      :active-ordering="sortState.activeOrdering"
      :active-segment="activeSegment"
      :segments-id="activeSegmentId"
      :is-fetching-list="isFetchingFresh"
      :has-applied-filters="hasAppliedFilters"
      :selected-contact-ids="selectedContactIds"
      @update:current-page="fetchContactsBasedOnContext"
      @search="searchContacts"
      @update:sort="handleSort"
      @apply-filter="fetchContactsBasedOnContext(1)"
      @clear-filters="handleClearFilters"
    >
      <div
        v-if="isFetchingFresh"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>

      <template v-else>
        <ContactsBulkActionBar
          v-if="hasSelection"
          :visible-contact-ids="visibleContactIds"
          :selected-contact-ids="selectedContactIds"
          :is-loading="isBulkActionLoading"
          @toggle-all="toggleSelectAll"
          @clear-selection="clearSelection"
          @assign-labels="assignLabels"
          @assign-agent="bulkAssignAgent"
          @bulk-update-field="bulkUpdateField"
          @delete-selected="openBulkDeleteDialog"
        />
        <ContactEmptyState
          v-if="showEmptyStateLayout"
          class="pt-14"
          :title="t('CONTACTS_LAYOUT.EMPTY_STATE.TITLE')"
          :subtitle="t('CONTACTS_LAYOUT.EMPTY_STATE.SUBTITLE')"
          :button-label="t('CONTACTS_LAYOUT.EMPTY_STATE.BUTTON_LABEL')"
          @create="createContact"
        />
        <div
          v-else-if="showEmptyText"
          class="flex items-center justify-center py-10"
        >
          <span class="text-base text-n-slate-11">
            {{ emptyStateMessage }}
          </span>
        </div>
        <div v-else class="flex flex-col gap-4 px-6 pt-4 pb-6">
          <div class="flex justify-end">
            <div class="flex items-center gap-1 p-1 rounded-lg bg-n-alpha-2">
              <Button
                icon="i-lucide-layout-grid"
                size="xs"
                :variant="!isTableView ? 'solid' : 'ghost'"
                :color="!isTableView ? 'blue' : 'slate'"
                :title="t('CONTACTS_LAYOUT.VIEW_TOGGLE.CARD')"
                @click="toggleViewType"
              />
              <Button
                icon="i-lucide-table"
                size="xs"
                :variant="isTableView ? 'solid' : 'ghost'"
                :color="isTableView ? 'blue' : 'slate'"
                :title="t('CONTACTS_LAYOUT.VIEW_TOGGLE.TABLE')"
                @click="toggleViewType"
              />
            </div>
          </div>
          <ContactsTableView
            v-if="isTableView"
            :contacts="allContacts"
            :fresh-fetch-key="freshFetchKey"
            :selected-contact-ids="selectedContactIds"
            :is-loading-more="isLoadingMore"
            :has-more="hasMoreContacts"
            :active-sort="sortState.activeSort"
            :active-ordering="sortState.activeOrdering"
            @toggle-contact="toggleContactSelection"
            @toggle-all="toggleSelectAll"
            @sort="handleSort"
            @load-more="handleLoadMore"
            @update-contact="handleUpdateContact"
          />
          <ContactsList
            v-else
            :contacts="allContacts"
            :selected-contact-ids="selectedContactIds"
            @toggle-contact="toggleContactSelection"
          />
          <Dialog
            v-if="selectedCount"
            ref="bulkDeleteDialogRef"
            type="alert"
            :title="bulkDeleteDialogTitle"
            :description="bulkDeleteDialogDescription"
            :confirm-button-label="bulkDeleteDialogConfirmLabel"
            :is-loading="isBulkActionLoading"
            @confirm="deleteContacts"
          />
        </div>
      </template>
    </ContactsListLayout>
  </div>
</template>
